const API_BASE_URL = 'https://backendhealthbridge.onrender.com/api';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Méthode générique pour les requêtes
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('token');

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erreur de l\'API');
      }

      return data;
    } catch (error) {
      console.error('Erreur API:', error);
      throw error;
    }
  }

  // Méthodes pour les médecins
  async getDoctors() {
    return this.request('/doctors');
  }

  async getDoctor(id) {
    return this.request(`/doctors/${id}`);
  }

  async createDoctor(doctorData) {
    return this.request('/doctors', {
      method: 'POST',
      body: JSON.stringify(doctorData),
    });
  }

  async updateDoctor(id, doctorData) {
    return this.request(`/doctors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(doctorData),
    });
  }

  async deleteDoctor(id) {
    return this.request(`/doctors/${id}`, {
      method: 'DELETE',
    });
  }

  // Méthodes pour les patients
  async getPatients(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/patients${queryString ? `?${queryString}` : ''}`);
  }

  async getPatientsByDoctor(doctorId) {
    return this.request(`/patients/doctor/${doctorId}`);
  }

  async getPatient(id) {
    return this.request(`/patients/${id}`);
  }

  async createPatient(patientData) {
    return this.request('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  }

  async updatePatient(id, patientData) {
    return this.request(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    });
  }

  async deletePatient(id) {
    return this.request(`/patients/${id}`, {
      method: 'DELETE',
    });
  }

  // Méthodes pour les maladies
  async getDiseases() {
    return this.request('/diseases');
  }

  async getDisease(id) {
    return this.request(`/diseases/${id}`);
  }

  async createDisease(diseaseData) {
    return this.request('/diseases', {
      method: 'POST',
      body: JSON.stringify(diseaseData),
    });
  }

  async updateDisease(id, diseaseData) {
    return this.request(`/diseases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(diseaseData),
    });
  }

  async deleteDisease(id) {
    return this.request(`/diseases/${id}`, {
      method: 'DELETE',
    });
  }

  // Méthodes pour les études
  async getStudies() {
    return this.request('/studies');
  }

  async getStudy(id) {
    return this.request(`/studies/${id}`);
  }

  async createStudy(studyData) {
    return this.request('/studies', {
      method: 'POST',
      body: JSON.stringify(studyData),
    });
  }

  async updateStudy(id, studyData) {
    return this.request(`/studies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(studyData),
    });
  }

  async deleteStudy(id) {
    return this.request(`/studies/${id}`, {
      method: 'DELETE',
    });
  }

  // Méthodes d'authentification
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async setOnline() {
    return this.request('/auth/online', {
      method: 'POST',
    });
  }

  async setOffline() {
    return this.request('/auth/offline', {
      method: 'POST',
    });
  }

  async getOnlineUsers() {
    return this.request('/auth/online-users');
  }

  async getAllUsers() {
    return this.request('/auth/all-users');
  }

  // Méthodes pour les messages
  async getConversations() {
    return this.request('/messages/conversations');
  }

  async getMessagesWithUser(userId) {
    return this.request(`/messages/${userId}`);
  }

  async sendMessage(receiverId, content) {
    return this.request('/messages/send', {
      method: 'POST',
      body: JSON.stringify({ receiverId, content }),
    });
  }

  // Méthodes pour les zones de santé
  async getHealthZones(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/health-zones${queryString ? `?${queryString}` : ''}`);
  }

  async getHealthZone(id) {
    return this.request(`/health-zones/${id}`);
  }

  async createHealthZone(healthZoneData) {
    return this.request('/health-zones', {
      method: 'POST',
      body: JSON.stringify(healthZoneData),
    });
  }

  async updateHealthZone(id, healthZoneData) {
    return this.request(`/health-zones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(healthZoneData),
    });
  }

  async deleteHealthZone(id) {
    return this.request(`/health-zones/${id}`, {
      method: 'DELETE',
    });
  }

  async getHealthZonesStats() {
    return this.request('/health-zones/stats');
  }

  // Méthodes pour les hôpitaux
  async getHospitals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/hospitals${queryString ? `?${queryString}` : ''}`);
  }

  async getHospital(id) {
    return this.request(`/hospitals/${id}`);
  }

  async createHospital(hospitalData) {
    return this.request('/hospitals', {
      method: 'POST',
      body: JSON.stringify(hospitalData),
    });
  }

  async updateHospital(id, hospitalData) {
    return this.request(`/hospitals/${id}`, {
      method: 'PUT',
      body: JSON.stringify(hospitalData),
    });
  }

  async deleteHospital(id) {
    return this.request(`/hospitals/${id}`, {
      method: 'DELETE',
    });
  }

  async getHospitalsStats() {
    return this.request('/hospitals/stats');
  }

  // Méthodes pour les consultations
  async getConsultations(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/consultations${queryString ? `?${queryString}` : ''}`);
  }

  async getConsultation(id) {
    return this.request(`/consultations/${id}`);
  }

  async createConsultation(consultationData) {
    return this.request('/consultations', {
      method: 'POST',
      body: JSON.stringify(consultationData),
    });
  }

  async updateConsultation(id, consultationData) {
    return this.request(`/consultations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(consultationData),
    });
  }

  async deleteConsultation(id) {
    return this.request(`/consultations/${id}`, {
      method: 'DELETE',
    });
  }

  // Méthodes pour les prescriptions
  async getPrescriptionsByConsultation(consultationId) {
    return this.request(`/prescriptions/consultation/${consultationId}`);
  }

  async getPrescription(id) {
    return this.request(`/prescriptions/${id}`);
  }

  async createPrescription(prescriptionData) {
    return this.request('/prescriptions', {
      method: 'POST',
      body: JSON.stringify(prescriptionData),
    });
  }

  async updatePrescription(id, prescriptionData) {
    return this.request(`/prescriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(prescriptionData),
    });
  }

  async deletePrescription(id) {
    return this.request(`/prescriptions/${id}`, {
      method: 'DELETE',
    });
  }

  // Méthodes pour les médicaments
  async getMedicaments(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/medicaments${queryString ? `?${queryString}` : ''}`);
  }

  async getMedicament(id) {
    return this.request(`/medicaments/${id}`);
  }

  async createMedicament(medicamentData) {
    return this.request('/medicaments', {
      method: 'POST',
      body: JSON.stringify(medicamentData),
    });
  }

  async updateMedicament(id, medicamentData) {
    return this.request(`/medicaments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(medicamentData),
    });
  }

  async deleteMedicament(id) {
    return this.request(`/medicaments/${id}`, {
      method: 'DELETE',
    });
  }

  async getMedicamentsStats() {
    return this.request('/medicaments/stats');
  }

  // Méthode pour enregistrer un téléchargement
  async logDownload(documentType, fileName) {
    return this.request('/activities/download', {
      method: 'POST',
      body: JSON.stringify({ documentType, fileName }),
    });
  }

  // Méthodes pour les activités
  async getActivities(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/activities${queryString ? `?${queryString}` : ''}`);
  }

  async createActivity(activityData) {
    return this.request('/activities', {
      method: 'POST',
      body: JSON.stringify(activityData),
    });
  }
}

// Créer une instance singleton
const apiService = new ApiService();

export default apiService;
